import React from 'react';
import { Box } from '@mui/material';
import { AccessTime as AccessTimeIcon, GpsFixed as GpsFixedIcon } from '@mui/icons-material';
import { PriorityLevel } from '../types/api';
import { formatDueDateDaysRemaining, formatEstimatedTime, getPriorityChipLabel, getPriorityDisplay, PriorityKind } from '../utils/pasoTags';

const CHIP_LABEL_TEXT_SX = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  lineHeight: 1,
};

interface TimeChipLabelProps {
  minutes: number;
}

interface PriorityIndicatorProps {
  value: PriorityLevel;
  size?: number;
}

interface PriorityChipLabelProps {
  kind: PriorityKind;
  value: PriorityLevel;
}

interface PrioritySelectValueProps {
  value: PriorityLevel;
  reserveDropdownArrowSpace?: boolean;
}

interface PriorityMenuOptionProps {
  value: PriorityLevel;
}

interface DueDateChipLabelProps {
  dueDate: string;
}

interface UnknownPriorityChipLabelProps {
  kind: PriorityKind;
}

export const NullSelectSymbol: React.FC = () => (
  <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }} aria-label="No value">
    —
  </Box>
);

export const TimeChipLabel: React.FC<TimeChipLabelProps> = ({ minutes }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0, position: 'relative', top: '0.025em' }}>
      <AccessTimeIcon sx={{ fontSize: 14, display: 'block' }} />
    </Box>
    <Box component="span" sx={{ position: 'relative', top: '0.025em' }}>
      {formatEstimatedTime(minutes)}
    </Box>
  </Box>
);

export const DueDateChipLabel: React.FC<DueDateChipLabelProps> = ({ dueDate }) => (
  (() => {
    const remaining = formatDueDateDaysRemaining(dueDate);
    if (!remaining) {
      return null;
    }

    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0, position: 'relative', top: '0.025em' }}>
          <GpsFixedIcon sx={{ fontSize: 14, display: 'block' }} />
        </Box>
        <Box component="span" sx={{ position: 'relative', top: '0.025em' }}>{remaining}</Box>
      </Box>
    );
  })()
);

export const PriorityIndicator: React.FC<PriorityIndicatorProps> = ({ value, size = 10 }) => {
  const display = getPriorityDisplay(value);

  if (!display) {
    return null;
  }

  if (display.shape === 'flat') {
    return (
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: size + 2,
          height: Math.max(4, Math.round(size * 0.6)),
          borderRadius: 0.5,
          backgroundColor: display.color,
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: 0,
        height: 0,
        borderLeft: `${size / 2}px solid transparent`,
        borderRight: `${size / 2}px solid transparent`,
        borderTop: display.shape === 'down' ? `${size}px solid ${display.color}` : 'none',
        borderBottom: display.shape === 'up' ? `${size}px solid ${display.color}` : 'none',
        flexShrink: 0,
      }}
    />
  );
};

export const PriorityChipLabel: React.FC<PriorityChipLabelProps> = ({ kind, value }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ...CHIP_LABEL_TEXT_SX }}>
    <PriorityIndicator value={value} />
    <Box component="span">{getPriorityChipLabel(kind)}</Box>
  </Box>
);

export const PrioritySelectValue: React.FC<PrioritySelectValueProps> = ({ value, reserveDropdownArrowSpace = false }) => {
  const display = getPriorityDisplay(value);

  if (!display) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%',
        boxSizing: 'border-box',
        pr: reserveDropdownArrowSpace ? 1.85 : 0,
      }}
    >
      <Box component="span">{display.label}</Box>
      <PriorityIndicator value={value} />
    </Box>
  );
};

export const PriorityMenuOption: React.FC<PriorityMenuOptionProps> = ({ value }) => <PrioritySelectValue value={value} />;

export const UnknownPriorityChipLabel: React.FC<UnknownPriorityChipLabelProps> = ({ kind }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...CHIP_LABEL_TEXT_SX }}>
    <Box component="span">{getPriorityChipLabel(kind)}</Box>
    <Box component="span" sx={{ fontWeight: 700 }}>?</Box>
  </Box>
);